import { useState, useEffect, useRef } from 'react';
import { Sparkles, Loader2, Info, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { ALL_FEATURES, type FeatureConfig } from '@/config/feature-catalog';
import type { WizardState } from './useWizardState';

interface StepProps {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
}

// For SaaS marketing sites, hide ecommerce/course features that don't apply.
// Only show features relevant to a content-driven marketing/sales site.
const SAAS_FEATURE_KEYS = new Set([
  'newsletter',
  'ai_features',
  'social_proof',
  'meilisearch',
  'job_queue_dashboard',
]);

// Sensible defaults for a SaaS marketing site — ecommerce forced off by the
// wizard job handler anyway, these are the features that actually matter.
const SAAS_DEFAULT_FEATURES: Record<string, boolean> = {
  ecommerce: false,
  courses: false,
  newsletter: true,
  ai_features: true,
  social_proof: true,
  meilisearch: false,
  job_queue_dashboard: true,
};

// Museum sites: focus on content + fundraising features. Ecommerce stays off
// unless museumHasGiftShop was checked in step 1 (which the wizard already
// flips to features.ecommerce=true at submit time). Courses, dropshipping,
// marketplace, etc. are simply not shown.
//
// fundraising / events / memberships are NOT toggleable here — they're
// force-enabled by db-setup.ts for every museum tenant, since they're the
// core museum feature stack.
const MUSEUM_FEATURE_KEYS = new Set([
  'ecommerce',         // shown but defaults off; reflects gift-shop opt-in
  'newsletter',
  'ai_features',
  'meilisearch',
  'reviews',
  'social_proof',
  'product_attributes',
  'wishlist',
]);

const MUSEUM_DEFAULT_FEATURES: Record<string, boolean> = {
  // Penny fundraising is force-enabled by the wizard job handler regardless;
  // it's not a "feature flag" the admin toggles in the wizard.
  newsletter: true,
  ai_features: true,
  meilisearch: true,
  reviews: false, // museums don't usually want product-style reviews on artifacts
  social_proof: false,
  // ecommerce mirrors museumHasGiftShop, set in step 1; not toggled here.
};

// Lodging sites: STR direct-booking. The booking flow replaces cart/checkout,
// so ecommerce / courses / dropshipping etc. are simply not shown.
// properties / bookings / reviews are NOT toggleable here — they're
// force-enabled by db-setup.ts for every lodging tenant, since they're the
// core STR feature stack. The keys here are the genuinely-optional features.
const LODGING_FEATURE_KEYS = new Set([
  'newsletter',
  'ai_features',
  'meilisearch',
  'social_proof',
  'reviews',           // shown — guest reviews can still be toggled off if the host runs reviews on Airbnb only
]);

const LODGING_DEFAULT_FEATURES: Record<string, boolean> = {
  newsletter: true,
  ai_features: true,
  meilisearch: false,  // small property catalog doesn't need full-text search by default
  social_proof: true,
  reviews: true,
  // ecommerce / courses / etc. forced off by site-creation.ts.
};

function FeatureToggle({
  feature,
  enabled,
  onToggle,
}: {
  feature: FeatureConfig;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex items-start gap-3 w-full text-left px-3 py-2.5 rounded-lg border transition-colors',
        enabled
          ? 'bg-green-50 border-green-200 hover:bg-green-100'
          : 'bg-stone-50 border-stone-200 hover:bg-stone-100'
      )}
    >
      {enabled ? (
        <ToggleRight className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
      ) : (
        <ToggleLeft className="w-5 h-5 text-stone-400 flex-shrink-0 mt-0.5" />
      )}
      <div className="min-w-0">
        <span className={cn('text-sm font-medium', enabled ? 'text-green-800' : 'text-text-secondary')}>
          {feature.label}
        </span>
        <p className="text-xs text-text-muted mt-0.5 leading-relaxed">{feature.hint}</p>
      </div>
    </button>
  );
}

export default function WizardStep3Features({ state, updateState }: StepProps) {
  const isSaas = state.siteType === 'saas';
  const isMuseum = state.siteType === 'museum';
  const isLodging = state.siteType === 'lodging';
  const [isLoading, setIsLoading] = useState(false);
  const [aiReasoning, setAiReasoning] = useState('');
  const hasFetched = useRef(false);

  const features: Record<string, boolean> = state.features || {};
  // SaaS sites get the marketing subset; museum sites get the cultural /
  // fundraising subset; lodging sites get the STR-relevant subset; ecommerce
  // sites get the full list. Avoids dozens of irrelevant toggles for
  // non-ecommerce site types.
  const visibleFeatures = isSaas
    ? ALL_FEATURES.filter((f) => SAAS_FEATURE_KEYS.has(f.key))
    : isMuseum
    ? ALL_FEATURES.filter((f) => MUSEUM_FEATURE_KEYS.has(f.key))
    : isLodging
    ? ALL_FEATURES.filter((f) => LODGING_FEATURE_KEYS.has(f.key))
    : ALL_FEATURES;

  function setFeatureEnabled(key: string, enabled: boolean) {
    updateState({
      features: { ...features, [key]: enabled },
    });
  }

  // Fetch AI recommendations on mount. SaaS sites skip the AI call and
  // use an opinionated default set instead — there's nothing for the AI
  // to usefully recommend from a short marketing-relevant feature list.
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (isSaas) {
      updateState({ features: SAAS_DEFAULT_FEATURES });
      setAiReasoning(
        'SaaS marketing sites use a curated feature set: newsletter, AI assistance, social proof, and the background job dashboard. Ecommerce, courses, and shipping features are turned off automatically.'
      );
      return;
    }
    if (isMuseum) {
      // Mirror the museumHasGiftShop opt-in into features.ecommerce. The
      // wizard job handler also enforces this at submit time, but setting
      // it here keeps the UI honest if the admin scrolls back.
      updateState({
        features: { ...MUSEUM_DEFAULT_FEATURES, ecommerce: !!state.museumHasGiftShop },
      });
      setAiReasoning(
        'Museum sites use a fundraising-first feature set. Penny (the AI fundraising agent) is enabled automatically. Ecommerce is on only if you opted into a gift shop in step 1; courses, dropshipping, and marketplace integrations are off.'
      );
      return;
    }
    if (isLodging) {
      updateState({ features: LODGING_DEFAULT_FEATURES });
      setAiReasoning(
        'Short-term-rental sites use a booking-first feature set. Properties, bookings, and reviews are enabled automatically — those are the core STR feature stack. Cart / checkout / orders / shipping are off (the booking flow replaces them); courses, dropshipping, and marketplace integrations are off.'
      );
      return;
    }
    fetchRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchRecommendations() {
    setIsLoading(true);
    try {
      const response = await api.post<{
        reasoning: string;
        features: Record<string, boolean>;
      }>('/admin/ai/generate/wizard-assist', {
        action: 'recommend-features',
        context: {
          storeName: state.storeName,
          businessDescription: state.businessDescription,
          tagline: state.tagline,
        },
      });

      if (response.reasoning) {
        setAiReasoning(response.reasoning);
      }
      if (response.features) {
        updateState({ features: response.features });
      }
    } catch {
      // Fall back to sensible defaults if AI fails
      const defaults: Record<string, boolean> = {};
      ALL_FEATURES.forEach((f) => {
        defaults[f.key] = f.group !== 'Advanced' && f.group !== 'Industry-Specific';
      });
      updateState({ features: defaults });
    } finally {
      setIsLoading(false);
    }
  }

  const enabledFeatures = visibleFeatures.filter((f) => features[f.key]);
  const disabledFeatures = visibleFeatures.filter((f) => !features[f.key]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 text-text-secondary">
          <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
          <span className="text-sm">Analyzing your business to recommend the right features...</span>
        </div>
        <div className="space-y-3 animate-pulse">
          <div className="h-16 bg-stone-100 rounded-lg" />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-14 bg-stone-100 rounded-lg" />
              ))}
            </div>
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-stone-100 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* AI reasoning banner */}
      {aiReasoning && (
        <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-xl">
          <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-purple-800 leading-relaxed space-y-2">
            {aiReasoning.split(/\n+|(?<=\.) (?=[A-Z])/).filter(Boolean).map((part, i) => {
              const trimmed = part.trim();
              if (!trimmed) return null;
              // Check if it looks like a bullet point or list item
              const isBullet = /^[-•*]/.test(trimmed) || /^\d+[.)]\s/.test(trimmed);
              if (isBullet) {
                return (
                  <div key={i} className="flex items-start gap-2 ml-1">
                    <span className="text-purple-400 mt-0.5">•</span>
                    <span>{trimmed.replace(/^[-•*]\s*/, '').replace(/^\d+[.)]\s*/, '')}</span>
                  </div>
                );
              }
              // Bold text between ** **
              const formatted = trimmed.split(/(\*\*[^*]+\*\*)/).map((seg, j) => {
                if (seg.startsWith('**') && seg.endsWith('**')) {
                  return <strong key={j} className="font-semibold">{seg.slice(2, -2)}</strong>;
                }
                return <span key={j}>{seg}</span>;
              });
              return <p key={i}>{formatted}</p>;
            })}
          </div>
        </div>
      )}

      {/* Two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Enabled */}
        <div>
          <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
            <ToggleRight className="w-4 h-4" />
            Enabled for Your Site
            <span className="text-xs font-normal text-text-muted">({enabledFeatures.length})</span>
          </h3>
          <div className="space-y-2">
            {enabledFeatures.map((feature) => (
              <FeatureToggle
                key={feature.key}
                feature={feature}
                enabled
                onToggle={() => setFeatureEnabled(feature.key, false)}
              />
            ))}
            {enabledFeatures.length === 0 && (
              <p className="text-sm text-text-muted py-4 text-center">No features enabled yet.</p>
            )}
          </div>
        </div>

        {/* Disabled */}
        <div>
          <h3 className="text-sm font-semibold text-stone-500 mb-3 flex items-center gap-2">
            <ToggleLeft className="w-4 h-4" />
            Available (Disabled)
            <span className="text-xs font-normal text-text-muted">({disabledFeatures.length})</span>
          </h3>
          <div className="space-y-2">
            {disabledFeatures.map((feature) => (
              <FeatureToggle
                key={feature.key}
                feature={feature}
                enabled={false}
                onToggle={() => setFeatureEnabled(feature.key, true)}
              />
            ))}
            {disabledFeatures.length === 0 && (
              <p className="text-sm text-text-muted py-4 text-center">All features enabled.</p>
            )}
          </div>
        </div>
      </div>

      {/* Note */}
      <div className="flex items-start gap-2 text-xs text-text-muted pt-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>Toggle any features above, then click Continue. You can always change these from your admin panel later.</p>
      </div>
    </div>
  );
}
